import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Route, Routes, useNavigate, useLocation, Navigate, useResolvedPath, NavigateFunction, Location } from 'react-router-dom';
import { useAdminLogout, useAdminState } from '../../context/Admin/admin';
import { Page } from '../Layout/Page/Page';
import { AdminPin } from './AdminPin/AdminPin';
import { IdentityVerification } from './IdentitiyVerification/IdentityVerification';
import { KeyManagementRoutes } from './KeyManagementRoutes/KeyManagementRoutes';
import { KioskInformation } from './KioskInformation/KioskInformation';
import { MfaQr } from './MfaQr/MfaQr';
import { SetupLogin } from './SetupLogin/SetupLogin';
import { AdminRoutePaths, AppRoutePaths, PathName } from '../../helpers/Routes';
import { AppointmentProvider } from '../../context/Appointment/appointment';
import { GoMotoComponent } from '../../API/GoMoto.types';
import { AdminLockerOperationProvider } from '../../context/AdminLockerOperation/adminLockerOperation';

/**
 * This component provides the initial setup and the routing to  all other components
 * that are a part of the Admin section on Kiosk.
 */
export const AdminRoutes: GoMotoComponent = () => {
    const adminState = useAdminState();
    const logout = useAdminLogout();
    const { pathname } = useResolvedPath('.');
    const navigate: NavigateFunction = useNavigate();
    const location: Location<any> = useLocation();

    const [authorized, setAuthorized] = useState<boolean>(false);
    const loggedOut = useRef<boolean>(false);

    useEffect(() => () => {
        if (!location.pathname.startsWith(AppRoutePaths.Admin) && !loggedOut.current) {
            loggedOut.current = true;
            logout(false);
        }
    }, [logout, location.pathname]);

    return (
        <Page
            showKioskBackground={false}
            showSettingsButton={true}
            suppressLocalizationButtons={location.pathname.includes(AdminRoutePaths.KeyManagement)}
            suppressBanner
        >
            <Routes>
                <Route
                    path={''}
                    element={
                        <MfaQr
                            onProceed={() => {
                                setAuthorized(true);
                                navigate(PathName(pathname, AdminRoutePaths.KeyManagement), { replace: true });
                            }}
                            onSkip={() => {
                                navigate(PathName(pathname, AdminRoutePaths.Pin));
                            }}
                        />
                    }
                />
                <Route
                    path={AdminRoutePaths.Pin}
                    element={
                        authorized ? (
                            <Navigate replace to={'/'} />
                        ) : (
                            <AdminPin
                                onProceed={() => {
                                    if (adminState.LoginSessionID) {
                                        setAuthorized(true);
                                        navigate(PathName(pathname, AdminRoutePaths.KeyManagement));
                                    }
                                    else {
                                        navigate(PathName(pathname, AdminRoutePaths.IdentityVerification));
                                    }
                                }}
                            />
                        )
                    }
                />
                <Route
                    path={AdminRoutePaths.IdentityVerification}
                    element={
                        authorized ? (
                            <Navigate replace to={'/'} />
                        ) : (
                            <IdentityVerification
                                onProceed={() => {
                                    setAuthorized(true);
                                    navigate(PathName(pathname, AdminRoutePaths.KeyManagement));
                                }}
                            />
                        )
                    }
                />
                <Route
                    path={AdminRoutePaths.KioskInformation}
                    element={
                        <KioskInformation />
                    }
                />
                <Route
                    path={AdminRoutePaths.SetupLogin}
                    element={
                        <SetupLogin />
                    }
                />
                <Route
                    path={PathName(AdminRoutePaths.KeyManagement, '*')}
                    element={
                        <AdminRoute authorized={authorized}>
                            <AppointmentProvider suppressBgCalls suppressRecallCalls>
                                <AdminLockerOperationProvider>
                                    <KeyManagementRoutes />
                                </AdminLockerOperationProvider>
                            </AppointmentProvider>
                        </AdminRoute>
                    }
                />
            </Routes>
        </Page>
    );
};

const AdminRoute: React.FC<PropsWithChildren<{ authorized: boolean; }>> = (props) => {
    return props.authorized ? <>{props.children}</> : <Navigate replace to={'/'} />;
};
